import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';

const AboutPage = () => {
  const navigate = useNavigate();

  const teamMembers = [
    {
      name: "Sarah Johnson",
      role: "Founder & CEO",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop",
      alt: "Portrait of Sarah Johnson, Founder and CEO of Share Good, smiling woman with long brown hair"
    },
    {
      name: "Michael Chen",
      role: "CTO",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop",
      alt: "Portrait of Michael Chen, Chief Technology Officer, man with glasses and professional attire"
    },
    {
      name: "Emily Rodriguez",
      role: "Head of Partnerships",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop",
      alt: "Portrait of Emily Rodriguez, Head of Partnerships, woman with curly dark hair smiling"
    },
    {
      name: "David Kim",
      role: "Head of Product",
      image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop",
      alt: "Portrait of David Kim, Head of Product, man with short dark hair in casual business attire"
    }
  ];

  const values = [
    {
      icon: "Heart",
      title: "Impact First",
      description: "Every decision we make is driven by maximizing positive social impact and creating meaningful change."
    },
    {
      icon: "Shield",
      title: "Transparency",
      description: "We believe in complete transparency in how donations are processed and the impact they create."
    },
    {
      icon: "Users",
      title: "Community",
      description: "Building a supportive community of givers who inspire and amplify each other's impact."
    },
    {
      icon: "Zap",
      title: "Innovation",
      description: "Leveraging technology to make charitable giving more accessible, engaging, and effective."
    }
  ];

  const stats = [
    { number: "$2.5M+", label: "Total Donated" },
    { number: "25K+", label: "Active Donors" },
    { number: "500+", label: "Nonprofit Partners" },
    { number: "150+", label: "Countries Reached" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-cream-50 to-white">
      <Header />

      {/* Hero Section */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Our Mission is to{' '}
              <span className="bg-gradient-to-r from-coral-500 to-orange-500 bg-clip-text text-transparent">
                Transform Giving
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              We're building the future of charitable giving - where technology meets compassion, 
              and every contribution creates measurable, lasting impact.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats?.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-coral-500 to-orange-500 bg-clip-text text-transparent mb-2">
                  {stat?.number}
                </div>
                <div className="text-gray-700 font-medium">{stat?.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-gray-700 text-lg leading-relaxed">
                <p>
                  Share Good was founded in 2020 with a simple but powerful vision: make charitable 
                  giving as easy, engaging, and impactful as possible.
                </p>
                <p>
                  We noticed that while people wanted to give back, the process was often confusing, 
                  impersonal, and lacked transparency. Donors couldn't easily track their impact, 
                  and nonprofits struggled to connect with passionate supporters.
                </p>
                <p>
                  Today, we've built a platform that brings together cutting-edge technology, 
                  verified nonprofits, and a vibrant community of givers - all working together 
                  to create positive change across the globe.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=800&h=600&fit=crop" 
                alt="Team collaboration at Share Good office showing diverse group of people working together on charitable giving initiatives"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values?.map((value, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all border border-orange-100"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-coral-500 to-orange-500 rounded-xl flex items-center justify-center mb-6">
                  <Icon name={value?.icon} size={32} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value?.title}</h3>
                <p className="text-gray-700 leading-relaxed">{value?.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Meet Our Team
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Passionate people working to make giving better for everyone
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers?.map((member, index) => (
              <div 
                key={index}
                className="text-center group"
              >
                <div className="mb-6 relative">
                  <img 
                    src={member?.image}
                    alt={member?.alt}
                    className="w-48 h-48 rounded-full mx-auto object-cover shadow-xl group-hover:shadow-2xl transition-shadow border-4 border-orange-100"
                  />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">{member?.name}</h3>
                <p className="text-coral-600 font-medium">{member?.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-coral-500 to-orange-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Join Us in Making a Difference
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Become part of a community that's changing the world, one donation at a time.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => navigate('/register')}
              className="bg-white text-coral-600 hover:bg-gray-100 px-8 py-4 text-lg shadow-xl"
            >
              Get Started
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/contact')}
              className="border-2 border-white text-white hover:bg-white/10 px-8 py-4 text-lg"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;